import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorBox } from 'components/Box'
import { BoxForm, Form, SaveButton, TextField } from 'components/Form'
import { useAbility } from 'providers/casl'
import { SetupRetargetingDeliveryItemsContext } from './context'
import { Schema, schema, schemaDefault } from './schema'
import { GamOrderRetargetingDealsDeliverySlice } from './useGetGamOrderRetargetingDealsDelivery'
import { useUpdateGamOrderRetargetingDealsDelivery } from './useUpdateGamOrderRetargetingDealsDelivery'

type Props = {
  errorMessage?: GamOrderRetargetingDealsDeliverySlice['errorMessage']
}

export const SetupRetargetingDeliveryItemsForm = ({ errorMessage }: Props): JSX.Element => {
  const ability = useAbility()
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'googleAdManagerConnection.setupRetargetingDeliveryItems.form',
  })
  const {
    formId,
    gamOrderRetargetingDealsDelivery,
    loading,
    setTouched,
    setValid,
    touched,
    valid,
  } = useContext(SetupRetargetingDeliveryItemsContext)
  const { errors: apiErrors, isPending, update } = useUpdateGamOrderRetargetingDealsDelivery()

  const canUpdate = ability.can(
    'update',
    'GlobalSetupFeature-googleAdManagerConnection-setupRetargetingDeliveryItems',
  )

  return (
    <Form<Schema>
      externalErrors={apiErrors}
      id={formId}
      loading={isPending}
      onSubmit={update}
      onTouched={setTouched}
      onValid={setValid}
      schema={schema}
      schemaDefaults={schemaDefault(gamOrderRetargetingDealsDelivery)}
    >
      {({ control }) => (
        <BoxForm>
          <TextField
            control={control}
            disabled={!canUpdate}
            label={t('proposalId.label')}
            name='proposalId'
            tooltip={{ content: t('proposalId.tooltip') }}
          />

          <TextField
            capslock
            control={control}
            disabled={!canUpdate}
            label={t('geo.label')}
            name='geo'
            tooltip={{ content: t('geo.tooltip') }}
          />

          <TextField
            capslock
            control={control}
            disabled={!canUpdate}
            label={t('currency.label')}
            name='currency'
            tooltip={{ content: t('currency.tooltip') }}
          />

          {errorMessage && errorMessage.length > 0 && <ErrorBox errorMessage={errorMessage} />}

          <SaveButton
            can={canUpdate}
            formId={formId}
            loading={loading}
            text={tc('form.action.start')}
            touched={touched}
            valid={valid}
          />
        </BoxForm>
      )}
    </Form>
  )
}
